import React, { useEffect, useState } from 'react';
import { FaPen, FaTrashAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import {
  getJournaux, getJournal, getStations, createJournal,
  ajouterMouvement, updateMouvement, deleteMouvement,
  cloturerJournal, demanderOtp, rouvrirJournal
} from '../services/api';

const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';

const GLOBAL_ROLES = ['superadmin', 'admin_siege', 'comptable', 'financier', 'fiscaliste', 'auditeur'];

const CATEGORIES_RECETTE = [
  'Vente carburant', 'Vente boutique', 'Vente lubrifiant', 'Vente gaz',
  'Lavage', 'Vidange', 'Location', 'Autre recette'
];
const CATEGORIES_DEPENSE = [
  'Achat marchandise', 'Salaires', 'Electricite', 'Eau', 'Telephone',
  'Entretien', 'Transport', 'Fournitures', 'Commission', 'Autre depense'
];
const MODES_PAIEMENT = [
  { value: 'especes', label: 'Especes' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'virement', label: 'Virement' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'mobile', label: 'Mobile Money' },
  { value: 'credit', label: 'Credit' },
];

const Journaux: React.FC = () => {
  const { user } = useAuth();
  const [journaux, setJournaux] = useState<any[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [stationId, setStationId] = useState('');
  const [loading, setLoading] = useState(true);

  const [selectedJournal, setSelectedJournal] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showNewForm, setShowNewForm] = useState(false);
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);

  const [showMouvForm, setShowMouvForm] = useState(false);
  const [editingMouv, setEditingMouv] = useState<any>(null);
  const [mouvType, setMouvType] = useState<'recette' | 'depense'>('recette');
  const [mouvCategorie, setMouvCategorie] = useState('');
  const [mouvDescription, setMouvDescription] = useState('');
  const [mouvMontant, setMouvMontant] = useState('');
  const [mouvMode, setMouvMode] = useState('especes');

  const [showClotureForm, setShowClotureForm] = useState(false);
  const [soldeFermeture, setSoldeFermeture] = useState('');
  const [saving, setSaving] = useState(false);

  // OTP
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpTelephone, setOtpTelephone] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpJournalId, setOtpJournalId] = useState<number | null>(null);
  const [devOtp, setDevOtp] = useState('');
  const [smsSent, setSmsSent] = useState(false);

  const isGlobal = GLOBAL_ROLES.includes(user?.role || '');
  const isGerant = user?.role === 'gerant';
  const isPompiste = user?.role === 'pompiste';

  useEffect(() => {
    if (isGlobal) {
      getStations().then(r => setStations(r.data)).catch(console.error);
    }
  }, []);

  useEffect(() => {
    loadJournaux();
  }, [stationId]);

  const loadJournaux = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (stationId) params.station_id = stationId;
      const res = await getJournaux(params);
      setJournaux(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openDetail = async (journalId: number) => {
    setDetailLoading(true);
    setShowDetail(true);
    try {
      const res = await getJournal(journalId);
      setSelectedJournal(res.data);
    } catch (err) { console.error(err); }
    setDetailLoading(false);
  };

  const closeDetail = () => {
    setShowDetail(false);
    setSelectedJournal(null);
    setShowMouvForm(false);
    setEditingMouv(null);
    setShowClotureForm(false);
  };

  const handleCreateJournal = async () => {
    if (!newDate) return alert('Veuillez selectionner une date');
    setSaving(true);
    try {
      // Pour le gerant, utiliser directement son station_id
      const sid = isGerant ? user?.station_id : (stationId ? Number(stationId) : null);
      if (!sid) return alert('Aucune station associee');
      const res = await createJournal({ station_id: sid, date_journal: newDate });
      setShowNewForm(false);
      loadJournaux();
      openDetail(res.data.id);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur lors de la creation');
    }
    setSaving(false);
  };

  const openMouvForm = (mouv?: any) => {
    if (mouv) {
      setEditingMouv(mouv);
      setMouvType(mouv.type_mouvement);
      setMouvCategorie(mouv.categorie);
      setMouvDescription(mouv.description || '');
      setMouvMontant(String(mouv.montant));
      setMouvMode(mouv.mode_paiement || 'especes');
    } else {
      setEditingMouv(null);
      setMouvType('recette');
      setMouvCategorie('');
      setMouvDescription('');
      setMouvMontant('');
      setMouvMode('especes');
    }
    setShowMouvForm(true);
  };

  const handleSaveMouvement = async () => {
    if (!mouvCategorie || !mouvMontant || Number(mouvMontant) <= 0) {
      return alert('Veuillez remplir la categorie et un montant valide');
    }
    setSaving(true);
    try {
      const data = {
        type_mouvement: mouvType,
        categorie: mouvCategorie,
        description: mouvDescription,
        montant: Number(mouvMontant),
        mode_paiement: mouvMode,
      };
      if (editingMouv) {
        await updateMouvement(selectedJournal.id, editingMouv.id, data);
      } else {
        await ajouterMouvement(selectedJournal.id, data);
      }
      setShowMouvForm(false);
      setEditingMouv(null);
      const res = await getJournal(selectedJournal.id);
      setSelectedJournal(res.data);
      loadJournaux();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  const handleDeleteMouvement = async (mouvId: number) => {
    if (!window.confirm('Supprimer ce mouvement ?')) return;
    try {
      await deleteMouvement(selectedJournal.id, mouvId);
      const res = await getJournal(selectedJournal.id);
      setSelectedJournal(res.data);
      loadJournaux();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleCloturer = async () => {
    if (!soldeFermeture || Number(soldeFermeture) < 0) return alert('Montant invalide');
    setSaving(true);
    try {
      await cloturerJournal(selectedJournal.id, { solde_fermeture: Number(soldeFermeture) });
      const res = await getJournal(selectedJournal.id);
      setSelectedJournal(res.data);
      setShowClotureForm(false);
      loadJournaux();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  // ── Demander OTP pour rouvrir ──
  const handleDemanderRouverture = (journalId: number) => {
    if (isGlobal) {
      // Admin: pas besoin d'OTP
      handleRouvrirDirect(journalId);
    } else {
      // Gerant: ouvrir le modal OTP
      setOtpJournalId(journalId);
      setOtpCode('');
      setOtpSent(false);
      setOtpTelephone('');
      setDevOtp('');
      setSmsSent(false);
      setShowOtpModal(true);
    }
  };

  const handleEnvoyerOtp = async () => {
    if (!otpJournalId) return;
    setOtpSending(true);
    try {
      const res = await demanderOtp(otpJournalId);
      setOtpTelephone(res.data.telephone || '');
      setSmsSent(res.data.sms_sent || false);
      setDevOtp(!res.data.sms_sent ? (res.data.dev_otp || '') : '');
      setOtpSent(true);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur lors de l\'envoi du code');
    }
    setOtpSending(false);
  };

  const handleVerifierOtpEtRouvrir = async () => {
    if (!otpJournalId || !otpCode) return alert('Veuillez saisir le code OTP');
    setSaving(true);
    try {
      await rouvrirJournal(otpJournalId, { otp: otpCode });
      setShowOtpModal(false);
      setOtpCode('');
      setOtpJournalId(null);
      // Refresh
      if (selectedJournal && selectedJournal.id === otpJournalId) {
        const res = await getJournal(otpJournalId);
        setSelectedJournal(res.data);
      }
      loadJournaux();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Code OTP invalide');
    }
    setSaving(false);
  };

  const handleRouvrirDirect = async (journalId: number) => {
    if (!window.confirm('Rouvrir ce journal pour modification ?')) return;
    try {
      await rouvrirJournal(journalId, {});
      if (selectedJournal && selectedJournal.id === journalId) {
        const res = await getJournal(journalId);
        setSelectedJournal(res.data);
      }
      loadJournaux();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const canEdit = (journal: any) => {
    if (!journal) return false;
    if (journal.statut === 'valide') return false;
    if (isGlobal) return true;
    if (isGerant && journal.statut === 'ouvert') return true;
    return false;
  };

  return (
    <div>
      <div className="page-header">
        <h2>Journaux Quotidiens</h2>
        {(isGerant || isGlobal) && (
          <button className="btn btn-primary" onClick={() => {
            setNewDate(new Date().toISOString().split('T')[0]);
            setShowNewForm(true);
          }}>
            + Nouveau Journal
          </button>
        )}
      </div>

      {/* Filtre station uniquement pour les admins */}
      {isGlobal && (
        <div className="filters">
          <select value={stationId} onChange={e => setStationId(e.target.value)}>
            <option value="">Toutes les stations</option>
            {stations.map((s: any) => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? <div className="loading">Chargement...</div> : (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              {isGlobal && <th>Station</th>}
              <th>Gerant</th>
              <th style={{textAlign:'right'}}>Ouverture</th>
              <th style={{textAlign:'right'}}>Recettes</th>
              <th style={{textAlign:'right'}}>Depenses</th>
              <th style={{textAlign:'right'}}>Fermeture</th>
              <th style={{textAlign:'right'}}>Ecart caisse</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {journaux.length === 0 ? (
              <tr><td colSpan={isGlobal ? 10 : 9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun journal trouve</td></tr>
            ) : journaux.map((j: any) => (
              <tr key={j.id}>
                <td>{new Date(j.date_journal).toLocaleDateString('fr-FR')}</td>
                {isGlobal && <td><strong>{j.station_nom}</strong></td>}
                <td>{j.gerant_prenom} {j.gerant_nom}</td>
                <td style={{textAlign:'right'}}>{formatMontant(j.solde_ouverture)}</td>
                <td style={{textAlign:'right', color: '#4caf50'}}>{formatMontant(j.total_recettes)}</td>
                <td style={{textAlign:'right', color: '#e53935'}}>{formatMontant(j.total_depenses)}</td>
                <td style={{textAlign:'right'}}>{formatMontant(j.solde_fermeture)}</td>
                <td style={{textAlign:'right', color: Math.abs(j.ecart_caisse) > 5000 ? '#e53935' : '#4caf50'}}>
                  {j.ecart_caisse > 0 ? '+' : ''}{formatMontant(j.ecart_caisse)}
                </td>
                <td>
                  <span className={`badge badge-${j.statut === 'cloture' ? 'success' : j.statut === 'valide' ? 'info' : 'warning'}`}>
                    {j.statut}
                  </span>
                </td>
                <td>
                  <button className="btn btn-sm" onClick={() => openDetail(j.id)}>
                    {j.statut === 'ouvert' && (isGerant || isGlobal) ? '✏️ Editer' : '👁 Voir'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ══════ MODAL: Nouveau Journal ══════ */}
      {showNewForm && (
        <div className="modal-overlay" onClick={() => setShowNewForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouveau Journal Quotidien</h3>
              <button className="modal-close" onClick={() => setShowNewForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Pour admin, choisir la station */}
              {isGlobal && (
                <div className="form-group">
                  <label>Station</label>
                  <select value={stationId} onChange={e => setStationId(e.target.value)}>
                    <option value="">-- Selectionner --</option>
                    {stations.map((s: any) => (
                      <option key={s.id} value={s.id}>{s.nom} - {s.ville}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Date du journal</label>
                <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
              </div>
              <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                Le solde d'ouverture sera calcule automatiquement a partir du journal precedent.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowNewForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleCreateJournal} disabled={saving}>
                {saving ? 'Creation...' : 'Creer le journal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ MODAL: Detail Journal ══════ */}
      {showDetail && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                Journal du {selectedJournal ? new Date(selectedJournal.date_journal).toLocaleDateString('fr-FR') : '...'}
                {selectedJournal && <span className={`badge badge-${selectedJournal.statut === 'cloture' ? 'success' : selectedJournal.statut === 'valide' ? 'info' : 'warning'}`} style={{ marginLeft: 12 }}>{selectedJournal.statut}</span>}
              </h3>
              <button className="modal-close" onClick={closeDetail}>&times;</button>
            </div>
            <div className="modal-body">
              {detailLoading ? <div className="loading">Chargement...</div> : selectedJournal && (
                <>
                  <div className="journal-recap">
                    <div className="recap-card">
                      <span className="recap-label">Solde ouverture</span>
                      <span className="recap-value">{formatMontant(selectedJournal.solde_ouverture)}</span>
                    </div>
                    <div className="recap-card recap-green">
                      <span className="recap-label">Total recettes</span>
                      <span className="recap-value">{formatMontant(selectedJournal.total_recettes)}</span>
                    </div>
                    <div className="recap-card recap-red">
                      <span className="recap-label">Total depenses</span>
                      <span className="recap-value">{formatMontant(selectedJournal.total_depenses)}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Solde theorique</span>
                      <span className="recap-value">
                        {formatMontant(Number(selectedJournal.solde_ouverture) + Number(selectedJournal.total_recettes) - Number(selectedJournal.total_depenses))}
                      </span>
                    </div>
                    {selectedJournal.statut !== 'ouvert' && (
                      <>
                        <div className="recap-card">
                          <span className="recap-label">Solde fermeture</span>
                          <span className="recap-value">{formatMontant(selectedJournal.solde_fermeture)}</span>
                        </div>
                        <div className={`recap-card ${Math.abs(selectedJournal.ecart_caisse) > 5000 ? 'recap-red' : 'recap-green'}`}>
                          <span className="recap-label">Ecart caisse</span>
                          <span className="recap-value">
                            {selectedJournal.ecart_caisse > 0 ? '+' : ''}{formatMontant(selectedJournal.ecart_caisse)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 10px' }}>
                    <h4>Mouvements ({selectedJournal.mouvements?.length || 0})</h4>
                    {canEdit(selectedJournal) && (
                      <button className="btn btn-primary btn-sm" onClick={() => openMouvForm()}>
                        + Ajouter un mouvement
                      </button>
                    )}
                  </div>

                  {selectedJournal.mouvements && selectedJournal.mouvements.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Type</th><th>Categorie</th><th>Description</th>
                          <th>Mode</th><th style={{textAlign:'right'}}>Montant</th>
                          {canEdit(selectedJournal) && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedJournal.mouvements.map((m: any) => (
                          <tr key={m.id}>
                            <td>
                              <span className={`badge badge-${m.type_mouvement === 'recette' ? 'success' : 'danger'}`}>
                                {m.type_mouvement === 'recette' ? '+ Recette' : '- Depense'}
                              </span>
                            </td>
                            <td>{m.categorie}</td>
                            <td style={{ color: '#666', fontSize: 12 }}>{m.description || '-'}</td>
                            <td style={{ fontSize: 12 }}>{m.mode_paiement}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: m.type_mouvement === 'recette' ? '#4caf50' : '#e53935' }}>
                              {formatMontant(m.montant)}
                            </td>
                            {canEdit(selectedJournal) && (
                              <td>
                                <button className="btn btn-sm" onClick={() => openMouvForm(m)} style={{ marginRight: 4 }}><FaPen /></button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteMouvement(m.id)}><FaTrashAlt /></button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 30, color: '#999', background: '#f9f9f9', borderRadius: 8 }}>
                      Aucun mouvement enregistre. {canEdit(selectedJournal) && 'Cliquez sur "+ Ajouter un mouvement" pour commencer.'}
                    </div>
                  )}

                  {showMouvForm && (
                    <div className="mouv-form">
                      <h4>{editingMouv ? 'Modifier le mouvement' : 'Nouveau mouvement'}</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Type</label>
                          <select value={mouvType} onChange={e => { setMouvType(e.target.value as any); setMouvCategorie(''); }}>
                            <option value="recette">Recette (+)</option>
                            <option value="depense">Depense (-)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Categorie</label>
                          <select value={mouvCategorie} onChange={e => setMouvCategorie(e.target.value)}>
                            <option value="">-- Selectionner --</option>
                            {(mouvType === 'recette' ? CATEGORIES_RECETTE : CATEGORIES_DEPENSE).map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Montant (FCFA)</label>
                          <input type="number" value={mouvMontant} onChange={e => setMouvMontant(e.target.value)} placeholder="0" min="0" />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ flex: 2 }}>
                          <label>Description (optionnel)</label>
                          <input type="text" value={mouvDescription} onChange={e => setMouvDescription(e.target.value)} placeholder="Details du mouvement..." />
                        </div>
                        <div className="form-group">
                          <label>Mode de paiement</label>
                          <select value={mouvMode} onChange={e => setMouvMode(e.target.value)}>
                            {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                        <button className="btn" onClick={() => { setShowMouvForm(false); setEditingMouv(null); }}>Annuler</button>
                        <button className="btn btn-primary" onClick={handleSaveMouvement} disabled={saving}>
                          {saving ? 'Enregistrement...' : editingMouv ? 'Modifier' : 'Ajouter'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedJournal && (
              <div className="modal-footer">
                {selectedJournal.statut === 'ouvert' && canEdit(selectedJournal) && !showClotureForm && (
                  <button className="btn btn-success" onClick={() => {
                    const theorique = Number(selectedJournal.solde_ouverture) + Number(selectedJournal.total_recettes) - Number(selectedJournal.total_depenses);
                    setSoldeFermeture(String(Math.round(theorique)));
                    setShowClotureForm(true);
                  }}>
                    Cloturer le journal
                  </button>
                )}
                {selectedJournal.statut === 'cloture' && (isGlobal || isGerant) &&
                  journaux.length > 0 && journaux[0].id === selectedJournal.id && (
                  <button className="btn btn-warning" onClick={() => handleDemanderRouverture(selectedJournal.id)}>
                    🔓 Rouvrir le journal
                  </button>
                )}

                {showClotureForm && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <label style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Solde de fermeture (FCFA) :</label>
                    <input type="number" value={soldeFermeture} onChange={e => setSoldeFermeture(e.target.value)}
                      style={{ width: 180 }} placeholder="Montant en caisse" />
                    <button className="btn" onClick={() => setShowClotureForm(false)}>Annuler</button>
                    <button className="btn btn-success" onClick={handleCloturer} disabled={saving}>
                      {saving ? '...' : 'Confirmer la cloture'}
                    </button>
                  </div>
                )}

                <button className="btn" onClick={closeDetail} style={{ marginLeft: 'auto' }}>Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ══════ MODAL: Verification OTP ══════ */}
      {showOtpModal && (
        <div className="modal-overlay" onClick={() => setShowOtpModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #FF6B35, #ED0000)' }}>
              <h3>🔐 Verification de securite</h3>
              <button className="modal-close" onClick={() => setShowOtpModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              {!otpSent ? (
                <>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>📱</div>
                  <p style={{ fontSize: 15, marginBottom: 20, color: '#555' }}>
                    Pour rouvrir ce journal, un code de verification sera envoye par SMS a votre numero de telephone.
                  </p>
                  <button className="btn btn-primary" onClick={handleEnvoyerOtp} disabled={otpSending} style={{ width: '100%', padding: '12px 20px', fontSize: 15 }}>
                    {otpSending ? 'Envoi en cours...' : 'Envoyer le code OTP'}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>{smsSent ? '📱' : '⚠️'}</div>
                  {smsSent ? (
                    <>
                      <p style={{ fontSize: 14, color: '#555', marginBottom: 6 }}>
                        Un code a 4 chiffres a ete envoye par SMS au
                      </p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#004A97', marginBottom: 16 }}>
                        {otpTelephone}
                      </p>
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: 14, color: '#e53935', marginBottom: 6 }}>
                        Le SMS n'a pas pu etre envoye. Voici votre code :
                      </p>
                      {devOtp && (
                        <div style={{
                          background: '#FFF8E1', border: '2px dashed #FFC72C', borderRadius: 8,
                          padding: '10px 16px', marginBottom: 16, display: 'inline-block'
                        }}>
                          <span style={{ fontSize: 28, fontWeight: 800, color: '#ED0000', letterSpacing: 6 }}>{devOtp}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="form-group" style={{ maxWidth: 180, margin: '0 auto 16px' }}>
                    <input
                      type="text"
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="0000"
                      maxLength={4}
                      style={{
                        textAlign: 'center', fontSize: 32, letterSpacing: 12,
                        fontWeight: 700, padding: '12px 16px'
                      }}
                      autoFocus
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleVerifierOtpEtRouvrir}
                    disabled={saving || otpCode.length !== 4}
                    style={{ width: '100%', padding: '12px 20px', fontSize: 15 }}
                  >
                    {saving ? 'Verification...' : 'Verifier et rouvrir le journal'}
                  </button>
                  <button
                    className="btn"
                    onClick={handleEnvoyerOtp}
                    disabled={otpSending}
                    style={{ marginTop: 12, fontSize: 13, color: '#888' }}
                  >
                    {otpSending ? 'Envoi...' : 'Renvoyer le code'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Journaux;
